import React, { useState } from "react";
import { auth, db } from "./firebase";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { 
  Activity, ArrowRight, ArrowLeft, Building2, ShieldCheck, CheckCircle2 
} from "lucide-react";

// Image import
import loginPic from "./loginpic.jpg"; 

const HospitalRegisterPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    hospitalName: "", email: "", password: "",
    registrationNumber: "", hospitalType: "General Hospital", 
    bedCapacity: "", emergencyServices: "No",
    address: "", website: "", ceoName: "", 
    ceoEmail: ""
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // Modified: No Backend API, just Firebase Email Verification
  const handleFinalRegister = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      // 1. Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // 2. Send verification link to the admin/CEO email
      await sendEmailVerification(user);
      
      // 3. Save data to Firestore
      await setDoc(doc(db, "hospitals", user.uid), {
        ...formData,
        verificationStatus: "pending_email_verification",
        joinedAt: new Date(),
      });

      setIsEmailSent(true);
      alert("Registration successful! Please check your email for the verification link. ✅");
      
      // Navigate to login after a small delay
      setTimeout(() => navigate("/login"), 3000);

    } catch (error) { 
      alert(error.message); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="h-screen w-full bg-white flex overflow-hidden font-sans">
      
      {/* LEFT: Branding Section */}
      <div className="hidden lg:flex w-1/2 relative bg-[#090B0E] overflow-hidden">
        <img src={loginPic} alt="Healthcare" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 to-transparent"></div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(37,99,235,0.4)]">
              <Activity className="text-white" size={22} />
            </div>
            <span className="text-xl font-black text-white tracking-tighter italic">Avaami<span className="text-blue-500">op</span></span>
          </div>

          <div className="max-w-md">
            <div className="h-1 w-12 bg-blue-500 mb-6 rounded-full"></div>
            <h2 className="text-4xl font-black text-white mb-10 leading-tight">
              Scale your Hospital <br />
              with <span className="text-blue-500 underline decoration-blue-500/30 underline-offset-8">Precision.</span>
            </h2>
            
            <div className="space-y-8 relative">
              <div className="absolute left-[15px] top-2 bottom-2 w-[1px] bg-white/5"></div>
              {[
                { s: 1, t: "Identity", d: "Organization credentials" },
                { s: 2, t: "Infrastructure", d: "Facility specifications" },
                { s: 3, t: "Verification", d: "Executive authorization" }
              ].map((item) => (
                <div key={item.s} className="flex gap-5 items-center relative z-10">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-700 ${
                    step >= item.s ? 'bg-blue-600 text-white' : 'bg-[#15181e] text-slate-600'
                  }`}>
                    {step > item.s ? <CheckCircle2 size={16} /> : item.s}
                  </div>
                  <div className="flex flex-col">
                    <h4 className={`text-xs font-bold uppercase tracking-widest ${step >= item.s ? 'text-white' : 'text-slate-600'}`}>{item.t}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.4em]">Avaamiop Core Systems © 2026</p>
        </div>
      </div>

      {/* RIGHT: Form Section */}
      <div className="w-full lg:w-1/2 flex flex-col bg-white overflow-hidden">
        
        <div className="p-8 flex justify-between items-center">
            <div className="lg:hidden flex items-center gap-2">
                <Activity className="text-blue-600" size={24} />
                <span className="font-black text-lg tracking-tighter">Avaami<span className="text-blue-600">op</span></span>
            </div>
            <div className="ml-auto flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Progress</span>
                <div className="h-1 w-24 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${(step/3)*100}%` }}></div>
                </div>
            </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-8 md:px-20 pb-20">
          <div className="w-full max-w-md">
            
            <header className="mb-8">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-none mb-2">
                   {step === 1 && "Start Onboarding"}
                   {step === 2 && "Infrastructure Info"}
                   {step === 3 && "Safety & Trust"}
                </h1>
                <p className="text-slate-400 text-sm">Please enter valid professional details.</p>
            </header>

            <div className="space-y-5">
              {step === 1 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-600 transition-colors"><Building2 size={18}/></div>
                    <input name="hospitalName" value={formData.hospitalName} onChange={handleChange} placeholder="Hospital Legal Name" className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:border-blue-500/20 focus:bg-white outline-none transition-all font-semibold" />
                  </div>
                  <input name="email" value={formData.email} onChange={handleChange} placeholder="Admin Email" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:border-blue-500/20 focus:bg-white outline-none transition-all font-semibold" />
                  <input name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Create Password" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:border-blue-500/20 focus:bg-white outline-none transition-all font-semibold" />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="grid grid-cols-2 gap-4">
                    <input name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} placeholder="License No." className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-semibold" />
                    <input name="bedCapacity" type="number" value={formData.bedCapacity} onChange={handleChange} placeholder="Beds" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-semibold" />
                  </div>
                  <select name="hospitalType" value={formData.hospitalType} onChange={handleChange} className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-semibold appearance-none cursor-pointer">
                    <option>General Hospital</option>
                    <option>Multispeciality</option>
                  </select>
                  <textarea name="address" value={formData.address} onChange={handleChange} placeholder="Official Address..." className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-semibold h-24 resize-none" />
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="p-6 bg-slate-900 rounded-3xl text-white flex items-center gap-4">
                    <div className="p-3 bg-blue-600 rounded-xl"><ShieldCheck size={24}/></div>
                    <div>
                        <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Security Protocol</p>
                        <p className="text-xs text-slate-300">Email verification will be sent.</p>
                    </div>
                  </div>
                  <input name="ceoName" value={formData.ceoName} onChange={handleChange} placeholder="CEO / MD Name" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-slate-800" />
                  <input name="ceoEmail" value={formData.ceoEmail} onChange={handleChange} placeholder="CEO Professional Email (for link)" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-semibold" />
                  
                  {isEmailSent && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-2xl text-center">
                       <p className="text-green-600 text-xs font-bold">Verification link sent to your email!</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-10 flex items-center gap-4">
              {step > 1 && (
                <button onClick={() => setStep(step - 1)} className="p-4 bg-slate-50 text-slate-400 rounded-2xl hover:bg-slate-100 transition-all border border-slate-100">
                  <ArrowLeft size={20} />
                </button>
              )}
              <button 
                onClick={step === 3 ? handleFinalRegister : () => setStep(step + 1)}
                disabled={loading}
                className="flex-1 bg-slate-900 text-white font-black py-4 rounded-2xl shadow-xl hover:bg-blue-600 transition-all flex items-center justify-center gap-3 uppercase tracking-[0.2em] text-[10px]"
              >
                {step === 3 ? (loading ? "Processing..." : "Complete Registration") : "Continue"}
                {step < 3 && <ArrowRight size={16} />}
              </button>
            </div>

            <button onClick={() => navigate('/login')} className="w-full mt-8 text-[10px] font-black text-slate-300 uppercase tracking-widest hover:text-blue-600 transition-all">
               Existing Institution? <span className="text-blue-600">Login</span>
            </button>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalRegisterPage;